import { useState } from "react";
import { FileJson, Globe, RefreshCw } from "lucide-react";
import { Button, Loader, cn } from "@wa/ui";
import { DomainSummary, type T } from "./DomainSummary";
import { DomainRawDialog } from "./DomainRawDialog";
import { useDomainCheck } from "./useDomainCheck";

/**
 * Domain and DNS check for one register entity, on the company detail page.
 *
 * The sales question this answers: how long has this company been online, who
 * runs their website and who runs their mail. Registration age separates a
 * fifteen-year-old site (a redesign lead) from a two-month-old one, and the
 * provider row says which platform they would be moving off.
 *
 * The lookup is on-demand, not automatic: it queries the live registry, and 90k
 * companies must not be swept because someone browsed the register.
 */
export function DomainCheckCard({ regCode, webUrl, t }: { regCode: string; webUrl: string | null; t: T }) {
  const { state, run } = useDomainCheck(regCode);
  const [rawOpen, setRawOpen] = useState(false);

  // Nothing to check - the register holds no website for this company.
  if (!webUrl) return null;

  return (
    <section className="rounded-xl border border-border bg-card/80 p-5 shadow-soft backdrop-blur">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Globe className="size-4 text-primary" /> {t("ariregister.dns.title")}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          {state.kind === "done" ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setRawOpen(true)}>
                <FileJson className="size-3.5" /> {t("ariregister.dns.raw")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => run(true)}>
                <RefreshCw className="size-3.5" /> {t("ariregister.dns.recheck")}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => run(false)} disabled={state.kind === "busy"}>
              <RefreshCw className={cn("size-3.5", state.kind === "busy" && "animate-spin")} />
              {t(state.kind === "busy" ? "ariregister.dns.checking" : "ariregister.dns.check")}
            </Button>
          )}
        </div>
      </div>

      {state.kind === "idle" && <p className="text-sm text-muted-foreground">{t("ariregister.dns.hint")}</p>}

      {state.kind === "busy" && (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      )}

      {state.kind === "error" && (
        <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          {t(state.messageKey)}
        </p>
      )}

      {state.kind === "done" && (
        <>
          <DomainSummary check={state.check} t={t} />
          <DomainRawDialog check={state.check} open={rawOpen} onOpenChange={setRawOpen} t={t} />
        </>
      )}
    </section>
  );
}
